import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getList, createList, updateList } from '../repository/listRepository';

export const ListEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [name, setName] = useState('');
  const [listType, setListType] = useState<'ranked' | 'unranked'>('ranked');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(isEditing);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      getList(id).then(list => {
        setName(list.name);
        setListType(list.list_type);
        setNotes(list.notes || '');
        setLoading(false);
      });
    }
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError('List name is required');
      return;
    }

    try {
      if (isEditing && id) {
        await updateList(id, { name: trimmed, list_type: listType, notes });
        navigate(`/lists/${id}`);
      } else {
        const newList = await createList(trimmed, listType, notes);
        navigate(`/lists/${newList.uuid}`);
      }
    } catch {
      setError('Failed to save list');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <h1 className="page-title">{isEditing ? 'Edit List' : 'New List'}</h1>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="name">Name</label>
          <input
            id="name"
            className="form-input"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Top 100 Books"
            autoFocus
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="listType">Type</label>
          <select
            id="listType"
            className="form-select"
            value={listType}
            onChange={e => setListType(e.target.value as 'ranked' | 'unranked')}
          >
            <option value="ranked">Ranked</option>
            <option value="unranked">Unranked</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            className="form-textarea"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Optional notes about this list"
          />
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="submit" className="btn btn-primary">
            {isEditing ? 'Save' : 'Create List'}
          </button>
          <button type="button" className="btn" onClick={() => navigate(isEditing ? `/lists/${id}` : '/')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
